import { useMutation, useQueryClient } from '@tanstack/react-query'
import { lcCoachingApi } from '@/api/lcCoaching'
import { learnerLibraryQueryKey } from '@/hooks/learner/useLearnerLibrary'
import { coachingKeys } from './useCoachingToday'
import { invalidateReviewNoteSources } from './useReviewNote'
import type { WorkbookMode } from '@/types/coaching'

export function useAdvanceLesson(learnerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (force: boolean) => lcCoachingApi.advanceLesson(learnerId, force),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: coachingKeys.learnerToday(learnerId) }),
      qc.invalidateQueries({ queryKey: coachingKeys.today() }),
      invalidateReviewNoteSources(qc, learnerId),
    ]),
  })
}

export function useCompleteLesson(
  learnerId: number,
  lessonId: number,
  workbookMode: WorkbookMode = 'IMMEDIATE',
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (force: boolean) => lcCoachingApi.completeLesson(learnerId, lessonId, force, workbookMode),
    onSuccess: async () => {
      await Promise.all([
        invalidateReviewNoteSources(qc, learnerId),
        qc.invalidateQueries({ queryKey: coachingKeys.learnerToday(learnerId) }),
        qc.invalidateQueries({ queryKey: coachingKeys.today() }),
        qc.invalidateQueries({ queryKey: ['coaching', 'learner', learnerId, 'library'] }),
        qc.invalidateQueries({ queryKey: learnerLibraryQueryKey }),
        qc.invalidateQueries({ queryKey: coachingKeys.lessonDetail(learnerId, lessonId) }),
        qc.invalidateQueries({ queryKey: coachingKeys.guideForLesson(learnerId, lessonId) }),
      ])
    },
  })
}

export function useLessonCompletionReadiness(
  learnerId: number,
  lessonId: number,
  workbookMode: WorkbookMode = 'IMMEDIATE',
) {
  return useMutation({
    mutationFn: () => lcCoachingApi.getLessonCompletionReadiness(learnerId, lessonId, workbookMode),
  })
}

export function useEndSession(learnerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (coachingNote?: string) => lcCoachingApi.endSession(learnerId, coachingNote),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: coachingKeys.learnerToday(learnerId) }),
      qc.invalidateQueries({ queryKey: coachingKeys.today() }),
      qc.invalidateQueries({ queryKey: ['coaching', 'classes'] }),
    ]),
  })
}

export function useManualCheckIn(learnerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => lcCoachingApi.manualCheckIn(learnerId),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: coachingKeys.learnerToday(learnerId) }),
      qc.invalidateQueries({ queryKey: coachingKeys.today() }),
      qc.invalidateQueries({ queryKey: ['coaching', 'classes'] }),
    ]),
  })
}

export function useMarkAbsent(learnerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (absenceReason?: string) => lcCoachingApi.markAbsent(learnerId, absenceReason),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: coachingKeys.learnerToday(learnerId) }),
      qc.invalidateQueries({ queryKey: coachingKeys.today() }),
      qc.invalidateQueries({ queryKey: ['coaching', 'classes'] }),
    ]),
  })
}

export function useCancelAbsence(learnerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => lcCoachingApi.cancelAbsence(learnerId),
    onSuccess: () => Promise.all([
      qc.invalidateQueries({ queryKey: coachingKeys.learnerToday(learnerId) }),
      qc.invalidateQueries({ queryKey: coachingKeys.today() }),
      qc.invalidateQueries({ queryKey: ['coaching', 'classes'] }),
    ]),
  })
}
